
import type { IAPIArticle, IAPIArticleAuthor } from '@hokify/magazine-interface';
import type { PropType } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-2';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import { defineComponent } from 'vue';
import Video from './VideoExtension';
import Fallback from './FallbackExtension';
import CallToAction from '../CallToAction.vue';
import SocialSharing from '../SocialSharing.vue';
import { format } from '../../helpers/datehelpers/format';
import ArticleAuthorBox from './ArticleAuthorBox.vue';
import ArticleDownloadSection from './ArticleDownloadSection.vue';
import ArticleTableOfContent from './ArticleTableOfContent.vue';

export default defineComponent({
	name: 'ArticlePage',
	components: {
		ArticleAuthorBox,
		ArticleDownloadSection,
		ArticleTableOfContent,
		CallToAction, // dont use hydrate wrappers as it is visible on desktop above the fold and it rerenders!
		SocialSharing,
		EditorContent
	},
	data() {
		const editor = undefined as Editor | undefined;

		return { editor, format };
	},
	computed: {
		isCompanyPage(): boolean {
			return this.mode === 'business';
		},
		bannerLink(): string {
			if (this.article.isCoreArticle) {
				return this.article.category?.namespace || '';
			}
			return `${this.article.category?.namespace || ''}/${
				this.article.category?.coreArticle?.alias || ''
			}`;
		},
		sideBanner(): { title: string; text: string; link: string } {
			switch (this.article.category?.identifier) {
				case 'case-studies':
				case 'blog':
					return {
						title: 'Finden Sie passende Kandidaten für Ihren Job!',
						text: 'Inserat schalten',
						link: '/business/pricing'
					};
				case 'bewerbung':
				case 'arbeitsrecht':
				case 'ausbildung-lehre':
					return {
						title: 'Erstelle jetzt kostenlos deinen Lebenslauf!',
						text: 'Lebenslauf erstellen',
						link: '/lebenslauf-erstellen'
					};
				case 'jobsuche':
				case 'arbeitsmarkt':
				case 'product-updates':
				default:
					return {
						title: 'Finde jetzt schneller deinen neuen Job!',
						text: 'Job finden',
						link: '/jobs'
					};
			}
		},
		formattedDate(): string {
			return format('dd.MM.yyyy', this.article.date);
		},
		estimatedReadingTime(): number {
			if (this.article?.content) {
				const words = this.article.content?.trim().split(/\s+/)?.length;
				// at least 1 minute
				return Math.floor(words / 300) + 1;
			}
			return 0;
		},
		author(): IAPIArticleAuthor | undefined {
			return this.article.author;
		},
		authorPageLink(): string {
			return this.author?.slug ? `/team/${this.author.slug}` : '';
		},
		authorName(): string {
			return this.author?.displayName || 'hokify Team';
		}
	},
	mounted() {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		this.editor = new Editor({
			content: this.article.content,
			extensions: [Document, Text, Fallback, Video],
			editable: false
		});
	},
	beforeDestroy() {
		this.editor?.destroy();
	},
	methods: {
		dateSchema(date): string {
			return format('yyyy-MM-dd', date);
		}
	},
	props: {
		article: { type: Object as PropType<IAPIArticle>, required: true },
		regionHasCoreArticle: { type: Boolean, required: true, default: true },
		mode: { type: String, required: true },
		isPreview: { type: Boolean }
	}
});
